<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Patient</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new patient account with basic details or register as guest
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-account-heart</v-icon>
                      Patient Registration
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Users
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Account Type Selection -->
            <v-card-text class="py-2 px-2">
              <div class="form-section mb-2">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-account-switch</v-icon>
                  Account Type
                </h3>
                
                <v-row>
                  <v-col cols="12">
                    <v-radio-group
                      v-model="accountType"
                      @change="onAccountTypeChange"
                      row
                      hide-details
                      class="account-type-radio"
                    >
                      <v-radio
                        label="Full Account"
                        value="full"
                        color="primary"
                        class="mr-4"
                      >
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                            <div>
                              <div class="font-weight-medium">Full Account</div>
                              <div class="text-caption grey--text">Complete profile with email and password</div>
                            </div>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio
                        label="Guest Account"
                        value="guest"
                        color="success"
                      >
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon color="success" class="mr-2">mdi-account-outline</v-icon>
                            <div>
                              <div class="font-weight-medium">Guest Account</div>
                              <div class="text-caption grey--text">Quick registration with minimal details</div>
                            </div>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Basic Information Section (Hidden for Guest Accounts) -->
                <div class="form-section mb-2" v-if="!isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Basic Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        placeholder="Enter first name"
                        outlined
                        :rules="[(v) => !!v || 'First name is required']"
                        :error-messages="errors.first_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.middle_name"
                        label="Middle Name (Optional)"
                        placeholder="Enter middle name"
                        outlined
                        :error-messages="errors.middle_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.last_name"
                        label="Last Name *"
                        placeholder="Enter last name"
                        outlined
                        :rules="[(v) => !!v || 'Last name is required']"
                        :error-messages="errors.last_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.user_name"
                        label="Username"
                        placeholder="Enter username (optional)"
                        outlined
                        :error-messages="errors.user_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-circle</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.gender_id"
                        :items="genderItems"
                        label="Gender"
                        placeholder="Select gender"
                        outlined
                        :error-messages="errors.gender_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-human-male-female</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.dob"
                        label="Date of Birth"
                        placeholder="YYYY-MM-DD"
                        type="date"
                        outlined
                        :error-messages="errors.dob"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.bloodgroup_id"
                        :items="bloodGroupItems"
                        label="Blood Group"
                        placeholder="Select blood group"
                        outlined
                        :error-messages="errors.bloodgroup_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-water</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.weight"
                        label="Weight (kg)"
                        placeholder="Enter weight"
                        type="number"
                        step="0.01"
                        outlined
                        :error-messages="errors.weight"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-scale-bathroom</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.height"
                        label="Height (cm)"
                        placeholder="Enter height"
                        type="number"
                        step="0.01"
                        outlined
                        :error-messages="errors.height"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-human-male-height</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-file-input
                        v-model="form.image_file"
                        label="Profile Image"
                        placeholder="Select profile image"
                        outlined
                        accept="image/*"
                        prepend-icon=""
                        :error-messages="errors.image_path"
                        class="professional-field"
                        hide-details="auto"
                        @change="onImageSelect"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-camera</v-icon>
                        </template>
                      </v-file-input>
                      <div v-if="form.image_preview" class="mt-2">
                        <v-img
                          :src="form.image_preview"
                          max-width="150"
                          max-height="150"
                          class="rounded"
                        ></v-img>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Guest Account Information Section (Only for Guest Accounts) -->
                <div class="form-section mb-2" v-if="isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="success" class="mr-2">mdi-account-outline</v-icon>
                    Guest Account Information
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="form.imei_number"
                        label="IMEI Number *"
                        placeholder="Enter device IMEI number"
                        outlined
                        :rules="imeiRules"
                        :error-messages="errors.imei_number"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-cellphone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                  </v-row>
                </div>

                <!-- Contact Information Section (Only for Full Accounts) -->
                <div class="form-section mb-2" v-if="!isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-card-account-mail</v-icon>
                    Contact Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        label="Email Address *"
                        placeholder="Enter email address"
                        outlined
                        type="email"
                        :rules="emailRules"
                        @input="errors.email = ''"
                        :error-messages="errors.email"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-email</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Phone Number"
                        placeholder="Enter phone number"
                        outlined
                        :error-messages="errors.phone"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-phone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.imei_number"
                        label="IMEI Number"
                        placeholder="Enter device IMEI number"
                        outlined
                        :error-messages="errors.imei_number"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-cellphone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Address Information Section (Hidden for Guest Accounts) -->
                <div class="form-section mb-2" v-if="!isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                    Address Information
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.address"
                        label="Full Address"
                        placeholder="Enter complete address"
                        outlined
                        rows="3"
                        :error-messages="errors.address"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-home</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="form.state"
                        :items="stateItems"
                        label="State"
                        placeholder="Select state"
                        outlined
                        @change="onStateChange"
                        :loading="statesLoading"
                        :error-messages="errors.state"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-map</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="form.city"
                        :items="cityItems"
                        label="City"
                        placeholder="Select city"
                        outlined
                        :disabled="!form.state"
                        :loading="citiesLoading"
                        :error-messages="errors.city"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-city</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.pincode"
                        label="Pincode"
                        placeholder="Enter pincode"
                        type="number"
                        outlined
                        :error-messages="errors.pincode"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-mailbox</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Security Section (Only for Full Account) -->
                <div class="form-section mb-2" v-if="!isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
                    Account Security
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.password"
                        label="Password *"
                        placeholder="Enter password"
                        outlined
                        type="password"
                        :rules="passwordRules"
                        :error-messages="errors.password"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-lock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.password_confirmation"
                        label="Confirm Password *"
                        placeholder="Confirm password"
                        outlined
                        type="password"
                        :rules="passwordConfirmationRules"
                        :error-messages="
                          errors.password_confirmation || confirmPasswordError
                        "
                        @blur="validatePasswordMatch"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-lock-check</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Account Settings Section (Hidden for Guest Accounts) -->
                <div class="form-section mb-2" v-if="!isGuestAccount">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Account Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-account-check</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Account Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the initial status for this patient account
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                            <v-chip
                              v-if="isGuestAccount"
                              small
                              color="info"
                              text-color="white"
                              class="ml-2"
                            >
                              Guest Account
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{ isGuestAccount ? 'Guest accounts require minimal information' : 'All required fields must be completed' }}
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      {{ isGuestAccount ? 'Save Guest' : 'Save Customer' }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PatientCreatePage",
  data() {
    return {
      valid: false,
      accountType: "full", // 'full' or 'guest'
      form: {
        first_name: "",
        middle_name: "",
        last_name: "",
        user_name: "",
        email: "",
        phone: "",
        imei_number: "",
        gender_id: null,
        bloodgroup_id: null,
        dob: "",
        weight: "",
        height: "",
        image_file: null,
        image_path: "",
        image_preview: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        password: "",
        password_confirmation: "",
        position_id: null, // Will be set to PATIENT position
        is_active: true,
        is_guest: false,
        soft_password: "123456",
      },
      errors: {},
      confirmPasswordError: [],
      isLoading: false,
      statesLoading: false,
      citiesLoading: false,
      genderItems: [],
      bloodGroupItems: [],
      stateItems: [],
      cityItems: [],
      positionItems: [],
    };
  },
  computed: {
    isGuestAccount() {
      return this.accountType === 'guest';
    },
    emailRules() {
      if (this.isGuestAccount) {
        return [
          (v) => !v || /.+@.+\..+/.test(v) || 'Email must be valid if provided'
        ];
      }
      return [
        (v) => !!v || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Email must be valid'
      ];
    },
    imeiRules() {
      if (this.isGuestAccount) {
        return [(v) => !!v || 'IMEI number is required for guest accounts'];
      }
      return [];
    },
    passwordRules() {
      if (this.isGuestAccount) {
        return [];
      }
      return [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 6) || 'Password must be at least 6 characters'
      ];
    },
    passwordConfirmationRules() {
      if (this.isGuestAccount) {
        return [];
      }
      return [
        (v) => !!v || 'Password confirmation is required',
        (v) => v === this.form.password || 'Passwords do not match'
      ];
    }
  },
  mounted() {
    this.form.company_id = this.company.id;
    this.form.role_id = "3";
    this.getMasters();
    this.loadStates();
    this.generateGuestCode();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=patient`);
        const masters = response.data;
        
        // Find PATIENT position
        const patientPosition = masters.positions?.find(
          position => position.name.toUpperCase() === 'PATIENT'
        );
        
        if (patientPosition) {
          this.form.position_id = patientPosition.id;
        }

        // Populate genders
        if (masters.genders) {
          this.genderItems = masters.genders.map(gender => ({
            text: gender.description,
            value: gender.id
          }));
        }

        // Populate blood groups
        if (masters.blood_groups) {
          this.bloodGroupItems = masters.blood_groups.map(bloodGroup => ({
            text: bloodGroup.description,
            value: bloodGroup.id
          }));
        }
        
        masters.positions?.forEach((position) => {
          this.positionItems.push({
            id: position.id,
            text: position.name,
            value: position.id,
          });
        });
      } catch (error) {
        console.log(error);
        window.location.href = "/auth/login";
      }
    },

    async loadStates() {
      try {
        this.statesLoading = true;
        // Using a third-party API for Indian states and cities
        const response = await fetch('https://api.countrystatecity.in/v1/countries/IN/states', {
          headers: {
            'X-CSCAPI-KEY': 'NHhvOEcyWk50N2Vna3VFTE00bFp3MjFKR0ZEOUhkZlg4RTk1MlJlaA==' // You should move this to environment variable
          }
        });
        const states = await response.json();
        
        this.stateItems = states.map(state => ({
          text: state.name,
          value: state.name,
          iso2: state.iso2
        }));
        this.statesLoading = false;
      } catch (error) {
        console.error('Error loading states:', error);
        this.statesLoading = false;
        // Fallback to basic Indian states
        this.stateItems = [
          { text: 'Andhra Pradesh', value: 'Andhra Pradesh' },
          { text: 'Karnataka', value: 'Karnataka' },
          { text: 'Maharashtra', value: 'Maharashtra' },
          { text: 'Tamil Nadu', value: 'Tamil Nadu' },
          { text: 'Delhi', value: 'Delhi' }
        ];
      }
    },

    async onStateChange() {
      this.form.city = "";
      this.cityItems = [];
      
      if (this.form.state) {
        try {
          this.citiesLoading = true;
          const selectedState = this.stateItems.find(state => state.value === this.form.state);
          
          if (selectedState && selectedState.iso2) {
            const response = await fetch(`https://api.countrystatecity.in/v1/countries/IN/states/${selectedState.iso2}/cities`, {
              headers: {
                'X-CSCAPI-KEY': 'NHhvOEcyWk50N2Vna3VFTE00bFp3MjFKR0ZEOUhkZlg4RTk1MlJlaA==' // You should move this to environment variable
              }
            });
            const cities = await response.json();
            
            this.cityItems = cities.map(city => ({
              text: city.name,
              value: city.name
            }));
          }
          this.citiesLoading = false;
        } catch (error) {
          console.error('Error fetching cities:', error);
          this.citiesLoading = false;
          // Fallback cities
          this.cityItems = [
            { text: 'Mumbai', value: 'Mumbai' },
            { text: 'Pune', value: 'Pune' },
            { text: 'Bangalore', value: 'Bangalore' },
            { text: 'Chennai', value: 'Chennai' },
            { text: 'Delhi', value: 'Delhi' }
          ];
        }
      }
    },

    onImageSelect(file) {
      if (file) {
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.image_preview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        // For now, we'll just store the file name as image_path
        // In a real implementation, you would upload the file to a server
        this.form.image_path = file.name;
      } else {
        this.form.image_preview = "";
        this.form.image_path = "";
      }
    },

    onAccountTypeChange() {
      this.form.is_guest = this.isGuestAccount;
      
      if (this.isGuestAccount) {
        // Clear all fields except guest-specific ones for guest accounts
        this.form.first_name = "";
        this.form.middle_name = "";
        this.form.last_name = "";
        this.form.user_name = "";
        this.form.email = "";
        this.form.phone = "";
        this.form.gender_id = null;
        this.form.bloodgroup_id = null;
        this.form.dob = "";
        this.form.weight = "";
        this.form.height = "";
        this.form.image_file = null;
        this.form.image_path = "";
        this.form.image_preview = "";
        this.form.address = "";
        this.form.city = "";
        this.form.state = "";
        this.form.pincode = "";
        this.form.password = "";
        this.form.password_confirmation = "";
        this.form.is_active = true; // Default to active for guests
      } else {
        // Clear guest-specific fields for full accounts
        this.form.imei_number = "";
      }
      
      // Clear validation errors
      this.errors = {};
      this.confirmPasswordError = [];
    },


    validatePasswordMatch() {
      if (!this.isGuestAccount && this.form.password && this.form.password_confirmation) {
        if (this.form.password !== this.form.password_confirmation) {
          this.confirmPasswordError = ["Passwords do not match"];
        } else {
          this.confirmPasswordError = [];
        }
      } else {
        this.confirmPasswordError = [];
      }
    },

    hasValidationErrors() {
      return (
        Object.keys(this.errors).length > 0 ||
        this.confirmPasswordError.length > 0 ||
        !this.valid
      );
    },

    async save() {
      // For guest accounts, validate only required guest fields
      if (this.isGuestAccount) {
        if (!this.form.imei_number) {
          this.$toast.error('IMEI number is required for guest accounts');
          return;
        }
      } else {
        // For full accounts, validate the form normally
        if (!this.$refs.form.validate()) {
          return;
        }
      }

      try {
        this.isLoading = true;
        this.errors = {};

        // Validate password match for full accounts
        if (!this.isGuestAccount) {
          this.validatePasswordMatch();
          if (this.confirmPasswordError.length > 0) {
            this.isLoading = false;
            return;
          }
        }

        // Create FormData for potential file uploads
        const formData = new FormData();

        if (this.isGuestAccount) {
          // For guest accounts, only send required fields
          formData.append('imei_number', this.form.imei_number);
          formData.append('is_guest', true);
          formData.append('is_active', true);
          formData.append('position_id', this.form.position_id);
          formData.append('company_id', this.form.company_id);
          formData.append('role_id', this.form.role_id);
        } else {
          // For full accounts, add all form fields
          Object.keys(this.form).forEach((key) => {
            if (this.form[key] !== null && this.form[key] !== '') {
              formData.append(key, this.form[key]);
            }
          });
        }

        const endpoint = this.isGuestAccount ? "/users/create_patient_guest" : "/patient/create";
        
        await axios.post(endpoint, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.isLoading = false;
        await this.saveUserTimestamp(null, this.form);
        
        const successMessage = this.isGuestAccount ? 
          'Patient guest account created successfully' : 
          'Patient account created successfully';
        this.$toast.success(successMessage);
        
        this.$router.push("/users");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error creating patient:", error);
          const errorMessage = this.isGuestAccount ?
            'Failed to create customer guest account' :
            'Failed to create customer account';
          this.$toast.error(errorMessage);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Account Type Radio */
.account-type-radio >>> .v-input--radio-group__input {
  flex-direction: row;
  flex-wrap: wrap;
}

.account-type-radio >>> .v-radio {
  margin-right: 2rem;
  margin-bottom: 1rem;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>